import { z } from 'zod';
import {
  BOARD_TITLE_MAX_LENGTH,
  CARD_TITLE_MAX_LENGTH,
  SHORT_LINK_MAX_LENGTH,
  boardRoleSchema,
  boardVisibilitySchema,
  cardNumberSchema,
} from './board';
import { ulidSchema } from './id';
import { LABEL_NAME_MAX_LENGTH, labelColourSchema } from './label';
import { LIST_TITLE_MAX_LENGTH } from './list';
import { notificationTypeSchema } from './notification';
import { RANK_MAX_LENGTH } from './rank';
import {
  WORKSPACE_DESCRIPTION_MAX_LENGTH,
  WORKSPACE_LOGO_URL_MAX_LENGTH,
  WORKSPACE_NAME_MAX_LENGTH,
  WORKSPACE_SHORT_NAME_MAX_LENGTH,
  WORKSPACE_WEBSITE_URL_MAX_LENGTH,
  workspaceRoleSchema,
  workspaceTypeSchema,
  workspaceVisibilitySchema,
} from './workspace';

const timestampSchema = z.string().min(1);
const nullableTimestampSchema = timestampSchema.nullable();
const rankSchema = z.string().max(RANK_MAX_LENGTH);
const shortLinkObjectSchema = z.string().max(SHORT_LINK_MAX_LENGTH);

/**
 * BE-02.1 — Phase 1 object-model contracts.
 *
 * These are canonical shared object shapes, not route response schemas and not
 * table mirrors. Route-specific projections such as `board.slug`, `card.path`,
 * `list.cardCount`, `boardMember.isGuest` and hydrated notification refs stay
 * on the routes that need them.
 */

export const workspaceObjectSchema = z.object({
  id: ulidSchema,
  name: z.string().max(WORKSPACE_NAME_MAX_LENGTH),
  shortName: z.string().max(WORKSPACE_SHORT_NAME_MAX_LENGTH),
  description: z.string().max(WORKSPACE_DESCRIPTION_MAX_LENGTH).nullable(),
  websiteUrl: z.string().max(WORKSPACE_WEBSITE_URL_MAX_LENGTH).nullable(),
  logoUrl: z.string().max(WORKSPACE_LOGO_URL_MAX_LENGTH).nullable(),
  workspaceType: workspaceTypeSchema.nullable(),
  visibility: workspaceVisibilitySchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export type WorkspaceObject = z.infer<typeof workspaceObjectSchema>;

export const boardObjectSchema = z.object({
  id: ulidSchema,
  workspaceId: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  shortLink: shortLinkObjectSchema,
  closed: z.boolean(),
  visibility: boardVisibilitySchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export type BoardObject = z.infer<typeof boardObjectSchema>;

export const listObjectSchema = z.object({
  id: ulidSchema,
  boardId: ulidSchema,
  title: z.string().max(LIST_TITLE_MAX_LENGTH),
  position: rankSchema,
  archived: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export type ListObject = z.infer<typeof listObjectSchema>;

export const cardObjectSchema = z.object({
  id: ulidSchema,
  boardId: ulidSchema,
  listId: ulidSchema,
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  description: z.string().nullable(),
  position: rankSchema,
  shortLink: shortLinkObjectSchema,
  cardNumber: cardNumberSchema,
  archived: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export type CardObject = z.infer<typeof cardObjectSchema>;

export const commentObjectSchema = z.object({
  id: ulidSchema,
  cardId: ulidSchema,
  /** Per-card comment number from 1, never reused — `commentSchema.number`. */
  number: z.number().int().positive(),
  authorUserId: ulidSchema.nullable(),
  body: z.string(),
  createdAt: timestampSchema,
  editedAt: nullableTimestampSchema,
});

export type CommentObject = z.infer<typeof commentObjectSchema>;

export const labelObjectSchema = z.object({
  id: ulidSchema,
  boardId: ulidSchema,
  name: z.string().max(LABEL_NAME_MAX_LENGTH).nullable(),
  colour: labelColourSchema,
  position: rankSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export type LabelObject = z.infer<typeof labelObjectSchema>;

export const workspaceMembershipObjectSchema = z.object({
  workspaceId: ulidSchema,
  userId: ulidSchema,
  role: workspaceRoleSchema,
  joinedAt: timestampSchema,
  updatedAt: timestampSchema,
});

export type WorkspaceMembershipObject = z.infer<typeof workspaceMembershipObjectSchema>;

export const boardMembershipObjectSchema = z.object({
  boardId: ulidSchema,
  userId: ulidSchema,
  role: boardRoleSchema,
  joinedAt: timestampSchema,
  updatedAt: timestampSchema,
});

export type BoardMembershipObject = z.infer<typeof boardMembershipObjectSchema>;

export const cardMembershipObjectSchema = z.object({
  cardId: ulidSchema,
  userId: ulidSchema,
  joinedAt: timestampSchema,
});

export type CardMembershipObject = z.infer<typeof cardMembershipObjectSchema>;

export const membershipObjectSchema = z.discriminatedUnion('kind', [
  workspaceMembershipObjectSchema.extend({ kind: z.literal('workspace') }),
  boardMembershipObjectSchema.extend({ kind: z.literal('board') }),
  cardMembershipObjectSchema.extend({ kind: z.literal('card') }),
]);

export type MembershipObject = z.infer<typeof membershipObjectSchema>;

export const notificationObjectSchema = z.object({
  id: ulidSchema,
  recipientUserId: ulidSchema,
  actorUserId: ulidSchema.nullable(),
  cardId: ulidSchema.nullable(),
  boardId: ulidSchema.nullable(),
  workspaceId: ulidSchema.nullable(),
  commentId: ulidSchema.nullable(),
  activityEntryId: ulidSchema.nullable(),
  type: notificationTypeSchema,
  groupedCount: z.number().int().min(1),
  read: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export type NotificationObject = z.infer<typeof notificationObjectSchema>;
